import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { programsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, TrendingUp, TrendingDown, DollarSign, Package,
  Wrench, Receipt, ShoppingCart, BarChart3, AlertTriangle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

export default function ProgramFinancialsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadFinancials = useCallback(async () => {
    setLoading(true);
    try {
      const res = await programsAPI.financials(id);
      setFinancials(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load financials');
      navigate(`/programs/${id}`);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadFinancials();
  }, [loadFinancials]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading financials...</p>
        </div>
      </div>
    );
  }

  if (!financials) return null;

  const f = financials;
  const variance = f.variance || {};
  const costVariancePositive = (variance.cost_variance || variance.variance || 0) >= 0;
  const saleVariancePositive = (variance.sale_variance || 0) >= 0;
  const actualMargin = variance.actual_margin || 0;
  const budgetMargin = variance.budgeted_margin || 0;

  return (
    <div className="space-y-4" data-testid="program-financials-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/programs/${id}`)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-bold">Program Financials</h1>
            <p className="text-xs text-muted-foreground">
              {f.program_name} | {f.customer_name}
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={loadFinancials} data-testid="refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Budget vs Actual Summary - 6 cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="bg-blue-50/50">
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-muted-foreground flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Budget (Sale)
            </p>
            <p className="text-lg font-bold font-mono text-blue-600">
              Rs {(f.budget?.sale || 0).toLocaleString('en-IN')}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50/50">
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-muted-foreground flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> Actual Sale
            </p>
            <p className="text-lg font-bold font-mono text-emerald-600">
              Rs {(f.actual?.dispatch_sales || 0).toLocaleString('en-IN')}
            </p>
          </CardContent>
        </Card>
        <Card className={saleVariancePositive ? 'bg-green-50/50' : 'bg-red-50/50'}>
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-muted-foreground">Sale Variance</p>
            <p className={`text-lg font-bold font-mono ${saleVariancePositive ? 'text-green-600' : 'text-red-600'}`}>
              {saleVariancePositive ? '+' : ''}{(variance.sale_variance || 0).toLocaleString('en-IN')}
            </p>
            <p className="text-[9px] text-muted-foreground">
              {variance.sale_variance_percent || 0}%
            </p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50/50">
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-muted-foreground flex items-center gap-1">
              <ShoppingCart className="w-3 h-3" /> Budget (Cost)
            </p>
            <p className="text-lg font-bold font-mono text-amber-600">
              Rs {(f.budget?.cost || 0).toLocaleString('en-IN')}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50/50">
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Receipt className="w-3 h-3" /> Actual Cost
            </p>
            <p className="text-lg font-bold font-mono text-purple-600">
              Rs {(f.actual?.total_cost || f.actual?.total || 0).toLocaleString('en-IN')}
            </p>
          </CardContent>
        </Card>
        <Card className={costVariancePositive ? 'bg-green-50/50' : 'bg-red-50/50'}>
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-muted-foreground">Cost Variance</p>
            <p className={`text-lg font-bold font-mono ${costVariancePositive ? 'text-green-600' : 'text-red-600'}`}>
              {costVariancePositive ? '+' : ''}{(variance.cost_variance || variance.variance || 0).toLocaleString('en-IN')}
            </p>
            <p className="text-[9px] text-muted-foreground">
              {variance.cost_variance_percent || variance.variance_percent || 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Margin Comparison */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-slate-50/50">
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-muted-foreground">Budgeted Margin</p>
            <p className={`text-xl font-bold font-mono ${budgetMargin >= 0 ? 'text-slate-700' : 'text-red-600'}`}>
              Rs {budgetMargin.toLocaleString('en-IN')}
            </p>
          </CardContent>
        </Card>
        <Card className={actualMargin >= 0 ? 'bg-green-50/50' : 'bg-red-50/50'}>
          <CardContent className="py-3 px-4">
            <p className="text-[10px] text-muted-foreground">Actual Margin</p>
            <p className={`text-xl font-bold font-mono ${actualMargin >= 0 ? 'text-green-700' : 'text-red-600'}`}>
              Rs {actualMargin.toLocaleString('en-IN')}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Actual Cost Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4" /> Actual Cost Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-2 rounded border bg-blue-50/30">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-blue-600" />
                  <span className="text-xs">Purchase Receipts</span>
                </div>
                <span className="text-sm font-bold font-mono">
                  Rs {(f.actual?.purchase_receipts || 0).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="flex items-center justify-between p-2 rounded border bg-amber-50/30">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-amber-600" />
                  <span className="text-xs">WO Material Consumption</span>
                </div>
                <span className="text-sm font-bold font-mono">
                  Rs {(f.actual?.wo_material_consumption || 0).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="flex items-center justify-between p-2 rounded border bg-purple-50/30">
                <div className="flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-purple-600" />
                  <span className="text-xs">Service Entries (Subcontracting)</span>
                </div>
                <span className="text-sm font-bold font-mono">
                  Rs {(f.actual?.service_entries || 0).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="flex items-center justify-between p-2 rounded border bg-slate-50/50">
                <div className="flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-slate-600" />
                  <span className="text-xs">Misc Receipts</span>
                </div>
                <span className="text-sm font-bold font-mono">
                  Rs {(f.actual?.misc_receipts || 0).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 rounded border-2 border-purple-200 bg-purple-50/50">
                <span className="text-sm font-semibold">Total Actual Cost</span>
                <span className="text-lg font-bold font-mono text-purple-700">
                  Rs {(f.actual?.total_cost || f.actual?.total || 0).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 rounded border-2 border-emerald-200 bg-emerald-50/50">
                <span className="text-sm font-semibold">Dispatch Sales (Revenue)</span>
                <span className="text-lg font-bold font-mono text-emerald-700">
                  Rs {(f.actual?.dispatch_sales || 0).toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cost by Item Type */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <Package className="w-4 h-4" /> Cost by Item Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(f.actual?.by_type || []).length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No cost data by type</p>
            ) : (
              <div className="space-y-2">
                {(f.actual?.by_type || []).sort((a, b) => b.amount - a.amount).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded border">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{item.type}</Badge>
                    </div>
                    <span className="text-sm font-bold font-mono">
                      Rs {(item.amount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cost & Sales Trend Over Time */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4" /> Cost & Sales Trend (Monthly)
          </CardTitle>
          <div className="flex gap-4 mt-1">
            <span className="text-[10px] flex items-center gap-1"><span className="w-3 h-3 bg-purple-500 rounded"></span> Cost</span>
            <span className="text-[10px] flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 rounded"></span> Sales</span>
          </div>
        </CardHeader>
        <CardContent>
          {(f.cost_trend || []).length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No trend data available</p>
          ) : (
            <div className="overflow-x-auto">
              <div className="flex gap-3 min-w-max pb-2">
                {(f.cost_trend || []).map((item, idx) => {
                  const maxVal = Math.max(...f.cost_trend.map(t => Math.max(t.cost || 0, t.sales || 0)));
                  const costPercent = maxVal > 0 ? ((item.cost || 0) / maxVal) * 100 : 0;
                  const salesPercent = maxVal > 0 ? ((item.sales || 0) / maxVal) * 100 : 0;
                  return (
                    <div key={idx} className="flex flex-col items-center gap-1 min-w-[70px]">
                      <div className="flex gap-1">
                        <div className="h-24 w-5 bg-slate-100 rounded relative overflow-hidden">
                          <div 
                            className="absolute bottom-0 left-0 right-0 bg-purple-500 rounded-t transition-all"
                            style={{ height: `${costPercent}%` }}
                            title={`Cost: Rs ${(item.cost || 0).toLocaleString('en-IN')}`}
                          />
                        </div>
                        <div className="h-24 w-5 bg-slate-100 rounded relative overflow-hidden">
                          <div 
                            className="absolute bottom-0 left-0 right-0 bg-emerald-500 rounded-t transition-all"
                            style={{ height: `${salesPercent}%` }}
                            title={`Sales: Rs ${(item.sales || 0).toLocaleString('en-IN')}`}
                          />
                        </div>
                      </div>
                      <span className="text-[9px] font-mono text-muted-foreground">{item.month}</span>
                      <div className="text-[9px] text-center">
                        <span className="text-purple-600 font-semibold">{((item.cost || 0) / 1000).toFixed(0)}K</span>
                        <span className="mx-0.5">/</span>
                        <span className="text-emerald-600 font-semibold">{((item.sales || 0) / 1000).toFixed(0)}K</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{f.work_orders_count || 0}</p>
            <p className="text-[10px] text-muted-foreground">Work Orders</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{f.inbound_challans_count || 0}</p>
            <p className="text-[10px] text-muted-foreground">Inbound Challans</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{f.dispatch_challans_count || 0}</p>
            <p className="text-[10px] text-muted-foreground">Dispatch Challans</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{f.service_entries_count || 0}</p>
            <p className="text-[10px] text-muted-foreground">Service Entries</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-slate-600">{f.misc_receipts_count || 0}</p>
            <p className="text-[10px] text-muted-foreground">Misc Receipts</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            <Receipt className="w-4 h-4" /> Recent Transactions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(f.recent_transactions || []).length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No transactions recorded yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Type</th>
                    <th className="p-2 text-left">Description</th>
                    <th className="p-2 text-right">Qty</th>
                    <th className="p-2 text-right">Unit Price</th>
                    <th className="p-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(f.recent_transactions || []).slice(0, 20).map((txn, idx) => {
                    const isSale = txn.type === 'dispatch_sale';
                    return (
                      <tr key={idx} className={`hover:bg-muted/30 ${isSale ? 'bg-emerald-50/30' : ''}`}>
                        <td className="p-2 font-mono text-[10px]">{txn.date || '-'}</td>
                        <td className="p-2">
                          <Badge variant="outline" className={`text-[9px] ${isSale ? 'border-emerald-500 text-emerald-700' : ''}`}>
                            {txn.type === 'purchase_receipt' ? 'Purchase' : 
                             txn.type === 'wo_consumption' ? 'WO Material' :
                             txn.type === 'service_entry' ? 'Service' :
                             txn.type === 'dispatch_sale' ? 'Sale' : 'Misc'}
                          </Badge>
                        </td>
                        <td className="p-2 max-w-[200px] truncate">
                          {txn.dc_number ? `DC: ${txn.dc_number} - ` : ''}{txn.item_name || txn.description || txn.wo_number || '-'}
                        </td>
                        <td className="p-2 text-right font-mono">{txn.quantity?.toFixed(2) || '-'}</td>
                        <td className="p-2 text-right font-mono">Rs {(txn.unit_price || 0).toLocaleString('en-IN')}</td>
                        <td className={`p-2 text-right font-mono font-semibold ${isSale ? 'text-emerald-700' : ''}`}>
                          {isSale ? '+' : ''}Rs {(txn.total || 0).toLocaleString('en-IN')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
